import { z } from 'zod'

import { ProductDetailResponseRaw } from '@/types'

function getSumPriceArticles(product: ProductDetailResponseRaw) {
  return product.articles
    ? product.articles
        .map((a) => {
          return a.price * a.qty || 0
        })
        .reduce((a, b) => a + b, 0)
    : 0
}

export function parseMeasurements(measurements?: string | object) {
  const measurementsSchema = z.object({
    referenceMeasurements: z
      .array(z.object({ metric: z.string(), imperial: z.string() }))
      .optional(),
    detailedMeasurements: z.array(
      z.object({
        type: z.string(),
        typeName: z.string(),
        textMetric: z.string(),
        textImperial: z.string(),
      })
    ),
  })
  if (measurements) {
    try {
      const parsed =
        typeof measurements === 'string'
          ? JSON.parse(measurements)
          : measurements
      const rich = measurementsSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Proxy may return a plain string like "120x140 cm" — not enough for structured display
      if (typeof parsed === 'string') {
        return {
          referenceMeasurements: [{ metric: parsed, imperial: parsed }],
          detailedMeasurements: [],
        }
      }
      return undefined
    } catch {
      // JSON.parse failed — the value is likely already a plain dimension string
      if (typeof measurements === 'string') {
        return {
          referenceMeasurements: [
            { metric: measurements, imperial: measurements },
          ],
          detailedMeasurements: [],
        }
      }
      return undefined
    }
  }
  return undefined
}

function parseMeasurementsImage(measurementsImage?: string | object) {
  if (measurementsImage) {
    try {
      const parsed =
        typeof measurementsImage === 'string'
          ? JSON.parse(measurementsImage)
          : measurementsImage
      // Try rich PIP format first
      const rich = imageSchema.safeParse(parsed)
      if (rich.success) return getImageProps(rich.data)
      // Fallback: simple proxy format { url, type, altText }
      if (parsed && typeof parsed === 'object' && 'url' in parsed) {
        return { href: parsed.url, alt: parsed.altText ?? '' }
      }
      return undefined
    } catch {
      // JSON.parse failed — not valid JSON string, ignore
      return undefined
    }
  }
  return undefined
}

function parseBenefits(benefits?: string | object) {
  const benefitsSchema = z.array(z.string())
  if (benefits) {
    try {
      const parsed =
        typeof benefits === 'string' ? JSON.parse(benefits) : benefits
      return benefitsSchema.parse(parsed)
    } catch (error) {
      console.error('Error parsing benefits: ', error)
      return undefined
    }
  }
  return undefined
}

function parseBenefitSummary(benefitSummary?: string | object) {
  const benefitSummarySchema = z.string()
  if (benefitSummary) {
    try {
      const parsed =
        typeof benefitSummary === 'string'
          ? JSON.parse(benefitSummary)
          : benefitSummary
      return benefitSummarySchema.parse(parsed)
    } catch {
      // JSON.parse failed — the value is likely already a plain string
      if (typeof benefitSummary === 'string') return benefitSummary
      return undefined
    }
  }
  return undefined
}

function parseGoodToKnows(goodToKnows?: string | object) {
  const goodToKnowsSchema = z.array(
    z.object({ text: z.string(), type: z.string() })
  )
  // Proxy may return a simple array of strings
  const simpleSchema = z.array(z.string())
  if (goodToKnows) {
    try {
      const parsed =
        typeof goodToKnows === 'string' ? JSON.parse(goodToKnows) : goodToKnows
      const rich = goodToKnowsSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Transform simple string array to expected shape
      const simple = simpleSchema.safeParse(parsed)
      if (simple.success) {
        return simple.data.map((text) => ({ text, type: '' }))
      }
      return undefined
    } catch (error) {
      console.error('Error parsing good to knows: ', error)
      return undefined
    }
  }
  return undefined
}

function parseMaterials(materials?: string | object) {
  const materialsSchema = z.array(
    z.object({
      partMaterials: z.array(
        z.object({
          partText: z.string().optional(),
          materialText: z.string(),
        })
      ),
    })
  )
  // Proxy may return a simple array of strings like ["Steel", "Glass"]
  const simpleSchema = z.array(z.string())
  if (materials) {
    try {
      const parsed =
        typeof materials === 'string' ? JSON.parse(materials) : materials
      const rich = materialsSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Transform simple string array to expected shape
      const simple = simpleSchema.safeParse(parsed)
      if (simple.success) {
        return simple.data.map((text) => ({
          partMaterials: [{ materialText: text }],
        }))
      }
      return undefined
    } catch (error) {
      console.error('Error parsing materials: ', error)
      return undefined
    }
  }
  return undefined
}

function parseCareInstructions(careInstructions?: string | object) {
  const careInstructionsSchema = z.array(
    z.object({
      careInstructionTexts: z.array(
        z.object({ careInstructionText: z.string() })
      ),
    })
  )
  // Proxy may return a simple array of strings
  const simpleSchema = z.array(z.string())
  if (careInstructions) {
    try {
      const parsed =
        typeof careInstructions === 'string'
          ? JSON.parse(careInstructions)
          : careInstructions
      const rich = careInstructionsSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Transform simple string array to expected shape
      const simple = simpleSchema.safeParse(parsed)
      if (simple.success) {
        return simple.data.map((text) => ({
          careInstructionTexts: [{ careInstructionText: text }],
        }))
      }
      return undefined
    } catch (error) {
      console.error('Error parsing care instructions: ', error)
      return undefined
    }
  }
  return undefined
}

function parseTechnicalCompliance(technicalCompliance?: string | object) {
  const technicalComplianceSchema = z.array(
    z.object({ heading: z.string(), value: z.string().optional() })
  )
  const simpleSchema = z.array(z.string())
  if (technicalCompliance) {
    try {
      const parsed =
        typeof technicalCompliance === 'string'
          ? JSON.parse(technicalCompliance)
          : technicalCompliance
      const rich = technicalComplianceSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Transform simple string array to expected shape
      const simple = simpleSchema.safeParse(parsed)
      if (simple.success) {
        return simple.data.map((text) => ({ heading: text, value: undefined }))
      }
      return undefined
    } catch (error) {
      console.error('Error parsing technical compliance: ', error)
      return undefined
    }
  }
  return undefined
}

const imageSchema = z.object({
  altText: z.string().optional(),
  id: z.string().optional(),
  peNo: z.string().optional(),
  typeName: z.string().optional(),
  typeNo: z.string().optional(),
  variants: z.array(
    z.object({
      quality: z.string().optional(),
      height: z.number().optional(),
      width: z.number().optional(),
      fileName: z.string().optional(),
      fileMimeType: z.string(),
      href: z.string(),
    })
  ),
})

function parseDesigners(designers?: string | object) {
  const designersSchema = z.array(
    z.object({
      name: z.string(),
      id: z.string().optional(),
      href: z.string().optional(),
    })
  )
  // Proxy may return a simple array of strings like ["Jon Karlsson"]
  const simpleSchema = z.array(z.string())
  if (designers) {
    try {
      const parsed =
        typeof designers === 'string' ? JSON.parse(designers) : designers
      const rich = designersSchema.safeParse(parsed)
      if (rich.success) return rich.data
      // Transform simple string array to expected shape
      const simple = simpleSchema.safeParse(parsed)
      if (simple.success) {
        return simple.data.map((name) => ({ name }))
      }
      return undefined
    } catch (error) {
      console.error('Error parsing designers: ', error)
      return undefined
    }
  }
  return undefined
}

function parseDurabilityIndexDocument(
  durabilityIndexDocument?: string | object
) {
  const durabilityIndexsSchema = z.object({
    id: z.string(),
    peNo: z.string(),
    typeNo: z.string(),
    typeName: z.string(),
    altText: z.string(),
    variants: z.array(
      z.object({
        quality: z.string(),
        height: z.number(),
        width: z.number(),
        fileMimeType: z.string(),
        fileName: z.string(),
        href: z.string().url(),
      })
    ),
  })

  if (durabilityIndexDocument) {
    try {
      const parsed =
        typeof durabilityIndexDocument === 'string'
          ? JSON.parse(durabilityIndexDocument)
          : durabilityIndexDocument
      return durabilityIndexsSchema.parse(parsed)
    } catch (error) {
      console.error('Error parsing durability index image: ', error)
      return undefined
    }
  }
  return undefined
}

function parseDurabilityIndexImage(durabilityIndexImage?: string | object) {
  const durabilityIndexImageSchema = z.object({
    id: z.string(),
    peNo: z.string(),
    typeNo: z.string(),
    typeName: z.string(),
    altText: z.string(),
    variants: z.array(
      z.object({
        quality: z.string(),
        height: z.number(),
        width: z.number(),
        fileMimeType: z.string(),
        fileName: z.string(),
        href: z.string().url(),
      })
    ),
  })

  if (durabilityIndexImage) {
    try {
      const parsed =
        typeof durabilityIndexImage === 'string'
          ? JSON.parse(durabilityIndexImage)
          : durabilityIndexImage
      return durabilityIndexImageSchema.parse(parsed)
    } catch (error) {
      console.error('Error parsing durability index image: ', error)
      return undefined
    }
  }
  return undefined
}

function parseAttachments(attachments?: string | object) {
  const attachmentsSchema = z.array(
    z.object({
      type: z.string(),
      href: z.string().url(),
    })
  )

  if (attachments) {
    try {
      const parsed =
        typeof attachments === 'string' ? JSON.parse(attachments) : attachments
      return attachmentsSchema.parse(parsed)
    } catch (error) {
      console.error('Error parsing attachments: ', error)
      return undefined
    }
  }
  return undefined
}

export function transformQueryData(data: ProductDetailResponseRaw) {
  const {
    benefits,
    good_to_knows,
    materials,
    care_instructions,
    technicalCompliance,
    media,
    durabilityIndexDocument,
    durabilityIndexImage,
  } = data

  const currencyCode =
    data.articles?.find(({ currency_code }) => !!currency_code)
      ?.currency_code || 'EUR'

  return {
    ...data,
    currencyCode,
    originalPrice: data.originalPrice ?? getSumPriceArticles(data),
    articlesPrice: data.originalPrice ?? getSumPriceArticles(data),
    benefits: parseBenefits(benefits),
    benefit_summary: parseBenefitSummary(data.benefit_summary),
    good_to_knows: parseGoodToKnows(good_to_knows),
    materials: parseMaterials(materials),
    care_instructions: parseCareInstructions(care_instructions),
    technicalCompliance: parseTechnicalCompliance(technicalCompliance),
    designers: parseDesigners(data.designers),
    measurements: parseMeasurements(data.measurements),
    measurementsImage: parseMeasurementsImage(data.measurementsImage),
    waterLabelImage: parseWaterLabelImageProps(media),
    durabilityIndexDocument: parseDurabilityIndexDocument(
      durabilityIndexDocument
    ),
    durabilityIndexImage: parseDurabilityIndexImage(durabilityIndexImage),
    attachments: parseAttachments(data.attachments),
  }
}

export type TransformedOffer = ReturnType<typeof transformQueryData>

function parseWaterLabelImageProps(media?: string | object[]) {
  if (media) {
    try {
      // Handle both string (needs parsing) and already-parsed object
      const images = typeof media === 'string' ? JSON.parse(media) : media
      const waterLabelImage = images?.find(
        (image: z.infer<typeof imageSchema>) =>
          image.typeName === 'UNIFIED_WATER_LABEL_IMAGE'
      )
      const imageProps = getWaterLabelImageProps(waterLabelImage)
      const waterLabelSymbol = images?.find(
        (image: z.infer<typeof imageSchema>) =>
          image.typeName === 'UNIFIED_WATER_LABEL_SYMBOL_IMAGE'
      )

      const symbolProps = getWaterLabelSymbolProps(waterLabelSymbol)
      return {
        ...imageProps,
        ...symbolProps,
      }
    } catch (error) {
      console.error('Error parsing technical water label image: ', error)
    }
  }
  return undefined
}

export function getImageProps(imageField: z.infer<typeof imageSchema>) {
  if (!imageField) return null

  const href = imageField.variants.find(
    ({ fileMimeType, quality }) =>
      fileMimeType === 'image/svg+xml' ||
      (fileMimeType === 'image/jpeg' && quality === 'S4')
  )?.href

  if (!href) return null

  return {
    href,
    alt: imageField.altText ?? '',
  }
}

export function getWaterLabelImageProps(
  imageField: z.infer<typeof imageSchema>
) {
  if (!imageField) return null

  const href = imageField.variants.find(
    ({ fileMimeType }) => fileMimeType === 'image/png'
  )?.href

  if (!href) return null

  return {
    href,
    alt: imageField.altText,
  }
}

export function getWaterLabelSymbolProps(
  imageField: z.infer<typeof imageSchema>
) {
  if (!imageField) return null

  const href = imageField.variants.find(
    ({ fileMimeType }) => fileMimeType === 'image/jpeg'
  )?.href

  if (!href) return null

  return {
    symbolHref: href,
    symbolAlt: imageField.altText,
  }
}
